/**
 * Field export — every catalogue field on every deal in this view, with the
 * value state (Stated / Derived / Suggested / Confirmed) and its source.
 *
 * The spreadsheet the field review works from (CRE-17, LPR-06). It goes
 * through POST /reports/generate like every other report export, so the same
 * `reports.export` permission and the same export rules apply.
 *
 * Dead deals are left out, as the pipeline report leaves them out, unless
 * "Include Dead deals" is ticked (verification 28 Sep, C10/C11: there was no
 * way to include them, and the file did not say they were missing). The
 * workbook's header states which rule it was built on.
 */
import { useState } from 'react'
import { Loader2, TableProperties } from 'lucide-react'
import { reportsService } from '@/services/loans'

/** Read a Blob through FileReader — available in every browser and in the
 *  test DOM, where Blob.text()/arrayBuffer() are not. */
function readBlob(blob: Blob, as: 'text' | 'buffer'): Promise<string | ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string | ArrayBuffer)
    reader.onerror = () => reject(reader.error)
    if (as === 'text') reader.readAsText(blob)
    else reader.readAsArrayBuffer(blob)
  })
}

async function blobDetail(data: unknown): Promise<string | null> {
  if (!(data instanceof Blob)) return null
  try {
    const parsed = JSON.parse((await readBlob(data, 'text')) as string)
    return typeof parsed?.detail === 'string' ? parsed.detail : null
  } catch {
    return null
  }
}

export function FieldExportButton({
  filters,
  filenameBase,
}: {
  /** The same filters the pipeline report exports with, so both describe one book. */
  filters: Record<string, unknown>
  filenameBase: string
}) {
  const [busy, setBusy] = useState(false)
  const [includeDead, setIncludeDead] = useState(false)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const run = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const response = await reportsService.generate({
        template: 'field_export',
        filters: includeDead ? { ...filters, include_dead: true } : filters,
        format: 'xlsx',
      })
      const blob = response.data as Blob
      const head = new Uint8Array((await readBlob(blob.slice(0, 2), 'buffer')) as ArrayBuffer)
      if (head[0] !== 0x50 || head[1] !== 0x4b) {
        throw new Error('the server did not return a workbook')
      }
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${filenameBase}_field_export${includeDead ? '_incl_dead' : ''}_${new Date().toISOString().slice(0, 10)}.xlsx`
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000)
      setMessage({ tone: 'ok', text: 'Field export saved' })
    } catch (cause: unknown) {
      const err = cause as { response?: { status?: number; data?: unknown }; message?: string }
      const detail = await blobDetail(err?.response?.data)
      setMessage({
        tone: 'error',
        text: err?.response?.status
          ? `Field export failed (HTTP ${err.response.status})${detail ? `: ${detail}` : ''}`
          : `Field export failed: ${err?.message ?? 'network error'}`,
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        aria-label="Field export"
        title="Every field on every deal in this view, with its value state (Stated / Derived / Suggested / Confirmed) and source — Excel"
        className="inline-flex h-7 items-center gap-1 rounded border border-amber-400/15 px-2 text-[10px] text-stone-400 transition-colors hover:border-amber-400/40 hover:text-amber-300 disabled:cursor-wait disabled:opacity-40"
      >
        {busy ? <Loader2 size={12} className="animate-spin" /> : <TableProperties size={12} />}
        Field export
      </button>
      <label
        title="Dead deals are left out unless this is ticked; the workbook's header says which"
        className="inline-flex cursor-pointer items-center gap-1 text-[10px] text-stone-500"
      >
        <input
          type="checkbox"
          checked={includeDead}
          onChange={(event) => setIncludeDead(event.target.checked)}
          disabled={busy}
          className="h-3 w-3 accent-amber-400"
        />
        Include Dead deals
      </label>
      {message ? (
        <span
          role={message.tone === 'error' ? 'alert' : 'status'}
          className={message.tone === 'error' ? 'text-[10px] text-red-300' : 'text-[10px] text-emerald-300'}
        >
          {message.text}
        </span>
      ) : null}
    </div>
  )
}
